import api from 'config/axiosConfig';
import { apiRoutes } from "util/apiRoutes";

const PUBLIC_VAPID_KEY = process.env.REACT_APP_PUBLIC_VAPID_KEY || '';

/**
 * Registers the service worker for push notifications
 */
export const registerServiceWorker = async (): Promise<ServiceWorkerRegistration | null> => {
  if ('serviceWorker' in navigator) {
    try {
      const registration = await navigator.serviceWorker.register('/service-worker.js');
      return registration;
    } catch (error) {
      console.error('Service Worker registration failed:', error);
      return null;
    }
  }
  console.warn('Service workers are not supported in this browser');
  return null;
};

/**
 * Checks if the browser supports push notifications
 */
export const isPushNotificationSupported = (): boolean => {
  return 'serviceWorker' in navigator && 'PushManager' in window;
};

/**
 * Requests permission for push notifications
 */
export const requestNotificationPermission = async (): Promise<NotificationPermission> => {
  if (!('Notification' in window)) {
    console.warn('This browser does not support notifications');
    return 'denied';
  }

  if (Notification.permission === 'granted') {
    return 'granted';
  }

  if (Notification.permission === 'denied') {
    console.warn('User has blocked notifications');
    return 'denied';
  }

  try {
    const permission = await Notification.requestPermission();
    return permission;
  } catch (error) {
    console.error('Error requesting notification permission:', error);
    return 'denied';
  }
};

/**
 * Subscribes the user to push notifications
 * @param registration - Service worker registration
 */
export const subscribeToPushNotifications = async (
  registration: ServiceWorkerRegistration
): Promise<PushSubscription | null> => {
  try {
    // Check for existing subscription
    let subscription = await registration.pushManager.getSubscription();

    if (subscription) {
      return subscription;
    }

    // Create new subscription
    subscription = await registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(PUBLIC_VAPID_KEY) as any
    });

    // Send the subscription to the server
    await saveSubscription(subscription);

    return subscription;
  } catch (error) {
    console.error('Failed to subscribe to push notifications:', error);
    return null;
  }
};

/**
 * Unsubscribes from push notifications
 */
export const unsubscribeFromPushNotifications = async (): Promise<boolean> => {
  try {
    const registration = await navigator.serviceWorker.ready;
    const subscription = await registration.pushManager.getSubscription();

    if (!subscription) {
      return true;
    }

    // Unsubscribe from push service
    const success = await subscription.unsubscribe();

    if (success) {
      // Remove subscription from server
      await deleteSubscription(subscription);
    }

    return success;
  } catch (error) {
    console.error('Error unsubscribing from push notifications:', error);
    return false;
  }
};

/**
 * Saves push subscription to the server
 * @param subscription - Push subscription object
 */
const saveSubscription = async (subscription: PushSubscription): Promise<void> => {
  try {
    await api.post(apiRoutes.user.pushSubscription, {
      subscription: subscription.toJSON()
    });
  } catch (error) {
    console.error('Failed to save push subscription:', error);
    throw error;
  }
};

/**
 * Deletes push subscription from the server
 * @param subscription - Push subscription object
 */
const deleteSubscription = async (subscription: PushSubscription): Promise<void> => {
  try {
    await api.delete(apiRoutes.user.pushSubscription, {
      data: { subscription: subscription.toJSON() }
    });
  } catch (error) {
    console.error('Failed to delete push subscription:', error);
    throw error;
  }
};

/**
 * Converts a base64 string to a Uint8Array for the application server key
 * @param base64String - Base64 encoded string
 */
const urlBase64ToUint8Array = (base64String: string): Uint8Array => {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding)
    .replace(/-/g, '+')
    .replace(/_/g, '/');

  const rawData = atob(base64);
  const outputArray = new Uint8Array(rawData.length);

  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }

  return outputArray;
}; 